import { useState, useEffect } from 'react';
import { Search, Copy, BookOpen, Lock, Shield, Wifi, Server, Cloud, AlertTriangle, FileText, Zap, Database, User, Code, File, BarChart2, Briefcase, FileCheck, Target, Mic, Cpu, Users, Info, X, ExternalLink, BookMarked, GitCompare } from 'lucide-react';
import { prompts } from '../data/prompts';

interface Prompt {
  id: string;
  title: string;
  description: string;
  prompt: string;
}

interface Category {
  id: string;
  name: string;
  icon: JSX.Element;
}

const CyberPromptTool = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('vulnerability-assessment');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showResourcesModal, setShowResourcesModal] = useState<boolean>(false);
  const [showModelComparisonModal, setShowModelComparisonModal] = useState<boolean>(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(false);

  // Check if this is the user's first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('cyberPromptToolVisited');
    if (!hasVisited) {
      setShowWelcomeModal(true);
    }
  }, []);

  const handleCloseWelcome = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      localStorage.setItem('cyberPromptToolVisited', 'true');
    }
    setShowWelcomeModal(false);
  };
  
  const categories: Category[] = [
    { id: 'vulnerability-assessment', name: 'Vulnerability Assessment', icon: <Shield size={18} /> },
    { id: 'penetration-testing', name: 'Penetration Testing', icon: <Lock size={18} /> },
    { id: 'threat-intelligence', name: 'Threat Intelligence', icon: <AlertTriangle size={18} /> },
    { id: 'malware-analysis', name: 'Malware Analysis', icon: <FileText size={18} /> },
    { id: 'network-security', name: 'Network Security', icon: <Wifi size={18} /> },
    { id: 'application-security', name: 'Application Security', icon: <Code size={18} /> },
    { id: 'cloud-security', name: 'Cloud Security', icon: <Cloud size={18} /> },
    { id: 'incident-response', name: 'Incident Response', icon: <Zap size={18} /> },
    { id: 'digital-forensics', name: 'Digital Forensics', icon: <Database size={18} /> },
    { id: 'security-awareness', name: 'Security Awareness', icon: <User size={18} /> },
    { id: 'security-policy', name: 'Security Policy & Compliance', icon: <File size={18} /> },
    { id: 'security-architecture', name: 'Security Architecture', icon: <Server size={18} /> },
    { id: 'devsecops', name: 'DevSecOps', icon: <Code size={18} /> },
    { id: 'risk-assessment', name: 'Risk Assessment', icon: <BarChart2 size={18} /> },
    { id: 'ciso-strategic', name: 'CISO Strategic', icon: <Briefcase size={18} /> },
    { id: 'compliance-security', name: 'Compliance Officer', icon: <FileCheck size={18} /> },
    { id: 'red-team-llm', name: 'Red Team LLM Testing', icon: <Target size={18} /> },
    { id: 'voice-ai-security', name: 'Voice AI Security', icon: <Mic size={18} /> },
    { id: 'security-reporting', name: 'Security Report Writing', icon: <FileText size={18} /> },
    { id: 'security-automation', name: 'Security Automation', icon: <Cpu size={18} /> },
    { id: 'iot-security', name: 'IoT Security', icon: <Wifi size={18} /> },
    { id: 'security-training', name: 'Security Training', icon: <Users size={18} /> },
  ];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedId(text);
        setTimeout(() => setCopiedId(null), 2000);
      });
  };

  const filteredCategories = categories.filter(category => 
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPrompts = selectedCategory ? 
    (prompts[selectedCategory as keyof typeof prompts]?.filter(prompt => 
      prompt.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      prompt.description.toLowerCase().includes(searchQuery.toLowerCase())
    ) || []) : [];

  const HowItWorksModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">How Cybersecurity Prompt Engineering Tool Works</h2>
          <button 
            onClick={() => setShowModal(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6">
          <h3 className="text-lg font-bold mb-3">Purpose</h3>
          <p className="mb-4">
            The Cybersecurity Prompt Engineering Tool is designed to help security professionals create effective and comprehensive prompts for AI language models when working on cybersecurity tasks. These prompts can be used with tools like ChatGPT, Claude, or other LLMs to generate high-quality cybersecurity content.
          </p>
          
          <h3 className="text-lg font-bold mb-3">Features</h3>
          <ul className="list-disc pl-5 mb-4 space-y-2">
            <li>Browse prompts organized by security domains (vulnerability assessment, penetration testing, etc.)</li>
            <li>Search for specific prompts based on keywords</li>
            <li>Copy prompts to use with your preferred AI assistant</li>
            <li>Customize templates by replacing placeholder text (indicated by [BRACKETS])</li>
          </ul>
          
          <h3 className="text-lg font-bold mb-3">How to Use</h3>
          <ol className="list-decimal pl-5 mb-4 space-y-2">
            <li>Select a security domain from the left sidebar</li>
            <li>Browse available prompt templates or search for specific topics</li>
            <li>Click "View Prompt" to see the full prompt template</li>
            <li>Click "Copy" to copy the prompt to your clipboard</li>
            <li>Paste the prompt into your AI tool of choice</li>
            <li>Replace the placeholder text (indicated by [BRACKETS]) with your specific information</li>
          </ol>
          
          <h3 className="text-lg font-bold mb-3">Benefits</h3>
          <p className="mb-4">
            Using well-crafted prompts with AI assistants can dramatically improve the quality, relevance, and accuracy of generated security content. Our tool provides expertly designed prompts that follow best practices in both prompt engineering and cybersecurity.
          </p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <h3 className="text-lg font-bold mb-2 flex items-center text-green-900">
              <BookMarked size={20} className="mr-2" />
              Want to Learn More?
            </h3>
            <p className="text-sm text-green-800 mb-3">
              Check out our comprehensive Resources page with the latest 2025 research from Anthropic, Google, OpenAI, OWASP, and more!
            </p>
            <button
              onClick={() => {
                setShowModal(false);
                setShowResourcesModal(true);
              }}
              className="flex items-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium"
            >
              <BookMarked size={18} className="mr-2" />
              View All Resources
            </button>
          </div>

          <h3 className="text-lg font-bold mb-3">Our Partners</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <a href="https://cyberagent.exchange/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 border rounded-lg hover:bg-gray-50">
              <span className="text-sm font-medium text-center">Cyber Agent Exchange</span>
            </a>
            <a href="https://airiskassess.com/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 border rounded-lg hover:bg-gray-50">
              <span className="text-sm font-medium text-center">AI Risk Assessment</span>
            </a>
            <a href="https://cybersecuritygpt.store" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 border rounded-lg hover:bg-gray-50">
              <span className="text-sm font-medium text-center">Cybersecurity GPT Store</span>
            </a>
            <a href="https://cisomarketplace.services/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 border rounded-lg hover:bg-gray-50">
              <span className="text-sm font-medium text-center">CISO Marketplace</span>
            </a>
            <a href="https://vibehack.dev/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 border rounded-lg hover:bg-gray-50">
              <span className="text-sm font-medium text-center">VibeHack</span>
            </a>
            <a href="https://compliance.airiskassess.com/" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 border rounded-lg hover:bg-gray-50">
              <span className="text-sm font-medium text-center">AI Risk Compliance</span>
            </a>
          </div>
          
          <p className="text-sm text-gray-600 italic">
            Note: The effectiveness of these prompts may vary depending on the AI system being used. You may need to adjust prompts based on the specific capabilities and limitations of your chosen AI assistant.
          </p>
        </div>
      </div>
    </div>
  );

  const ResourcesModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">Latest Research & Resources (2025)</h2>
          <button
            onClick={() => setShowResourcesModal(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <p className="mb-6 text-gray-700">
            Our prompts are based on the latest research and best practices from leading AI and cybersecurity organizations.
            Explore these resources to deepen your understanding of prompt engineering and LLM security.
          </p>

          <div className="space-y-6">
            {/* Anthropic Section */}
            <div>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <Shield size={20} className="mr-2 text-blue-600" />
                Anthropic (Claude)
              </h3>
              <ul className="space-y-2 ml-7">
                <li>
                  <a href="https://docs.anthropic.com/en/docs/build-with-claude/prompt-engineering/overview" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Prompt Engineering Overview - Claude Docs <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://docs.claude.com/en/docs/build-with-claude/prompt-engineering/claude-4-best-practices" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Claude 4 Prompt Engineering Best Practices <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://www.anthropic.com/engineering/claude-code-sandboxing" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Making Claude Code More Secure with Sandboxing <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://www.anthropic.com/engineering/effective-context-engineering-for-ai-agents" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Effective Context Engineering for AI Agents <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Google Section */}
            <div>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <Zap size={20} className="mr-2 text-green-600" />
                Google (Gemini)
              </h3>
              <ul className="space-y-2 ml-7">
                <li>
                  <a href="https://ai.google.dev/gemini-api/docs/prompting-strategies" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Gemini Prompt Design Strategies <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://cloud.google.com/blog/topics/threat-intelligence/ai-nist-nice-prompt-library-gemini" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    NIST NICE Cybersecurity Prompt Library (6,000+ Prompts) <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://www.pluralsight.com/courses/gemini-prompt-engineering-cyber-security" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Gemini: Prompt Engineering for Cyber Security <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
              </ul>
            </div>

            {/* OpenAI Section */}
            <div>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <Cpu size={20} className="mr-2 text-purple-600" />
                OpenAI (GPT-4)
              </h3>
              <ul className="space-y-2 ml-7">
                <li>
                  <a href="https://cookbook.openai.com/examples/gpt4-1_prompting_guide" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    GPT-4.1 Prompting Guide <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://platform.openai.com/docs/guides/prompt-engineering" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Prompt Engineering - OpenAI API <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://openai.com/index/prompt-injections/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Understanding Prompt Injections <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
              </ul>
            </div>

            {/* OWASP & Security Standards */}
            <div>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <AlertTriangle size={20} className="mr-2 text-red-600" />
                OWASP & Security Standards
              </h3>
              <ul className="space-y-2 ml-7">
                <li>
                  <a href="https://genai.owasp.org/llmrisk/llm01-prompt-injection/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    LLM01:2025 - Prompt Injection (OWASP #1 Risk) <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://www.invicti.com/blog/web-security/owasp-top-10-risks-llm-security-2025" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    OWASP Top 10 for LLMs 2025 <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://owasp.org/www-project-ai-testing-guide/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    OWASP AI Testing Guide <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Attack & Defense Research */}
            <div>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <Target size={20} className="mr-2 text-orange-600" />
                Attack & Defense Research (2025)
              </h3>
              <ul className="space-y-2 ml-7">
                <li>
                  <a href="https://arxiv.org/abs/2404.02151" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Jailbreaking Leading Safety-Aligned LLMs (ICLR 2025) <ExternalLink size={14} className="ml-1" />
                  </a>
                  <span className="text-sm text-gray-600 ml-6 block">100% attack success rate on GPT-4o, Claude, Llama, Gemma</span>
                </li>
                <li>
                  <a href="https://www.theregister.com/2025/11/14/ai_guardrails_prompt_injections_echogram_tokens/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    EchoGram Tokens Bypass AI Guardrails (Nov 2025) <ExternalLink size={14} className="ml-1" />
                  </a>
                  <span className="text-sm text-gray-600 ml-6 block">Simple tokens like "=coffee" flip guardrail verdicts</span>
                </li>
                <li>
                  <a href="https://developer.nvidia.com/blog/securing-agentic-ai-how-semantic-prompt-injections-bypass-ai-guardrails/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Securing Agentic AI - NVIDIA (2025) <ExternalLink size={14} className="ml-1" />
                  </a>
                  <span className="text-sm text-gray-600 ml-6 block">Semantic prompt injections bypass AI guardrails</span>
                </li>
                <li>
                  <a href="https://arxiv.org/abs/2504.11168" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Bypassing LLM Guardrails (2025) <ExternalLink size={14} className="ml-1" />
                  </a>
                  <span className="text-sm text-gray-600 ml-6 block">Up to 100% evasion on Azure Prompt Shield, Meta Prompt Guard</span>
                </li>
                <li>
                  <a href="https://blogs.cisco.com/ai/open-model-vulnerability-analysis" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Death by a Thousand Prompts - Cisco AI Defense <ExternalLink size={14} className="ml-1" />
                  </a>
                  <span className="text-sm text-gray-600 ml-6 block">Multi-turn attacks achieve 60%+ success vs 10-15% single-turn</span>
                </li>
              </ul>
            </div>

            {/* Red Teaming & Frameworks */}
            <div>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <FileCheck size={20} className="mr-2 text-indigo-600" />
                Red Teaming & Frameworks
              </h3>
              <ul className="space-y-2 ml-7">
                <li>
                  <a href="https://www.promptfoo.dev/docs/red-team/nist-ai-rmf/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    NIST AI Risk Management Framework <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://ajithp.com/2025/07/13/red-teaming-large-language-models-playbook/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Red Teaming LLM Playbook (2025) <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://www.mend.io/blog/best-ai-red-teaming-tools-top-7-solutions-in-2025/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    AI Red Teaming: 7 Best Tools in 2025 <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Additional Resources */}
            <div>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <BookOpen size={20} className="mr-2 text-gray-600" />
                Additional Learning Resources
              </h3>
              <ul className="space-y-2 ml-7">
                <li>
                  <a href="https://ai.google.dev/gemini-api/docs/prompting-strategies" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Google Gemini Prompt Design Strategies <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://developers.google.com/machine-learning/resources/prompt-eng" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    Google ML Prompt Engineering Resources <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://www.promptingguide.ai/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    The Prompt Engineering Guide <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://github.com/dair-ai/Prompt-Engineering-Guide" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                    DAIR.AI Prompt Engineering Guide (GitHub) <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> These resources are regularly updated to reflect the latest research and developments
              in AI security and prompt engineering. Last updated: November 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const ModelComparisonModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">LLM Model Comparison for Cybersecurity</h2>
          <button
            onClick={() => setShowModelComparisonModal(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <p className="mb-6 text-gray-700">
            Different LLM models have unique strengths for cybersecurity tasks. This guide helps you choose the right model
            for your specific needs and understand how to prompt each one effectively.
          </p>

          <div className="space-y-6">
            {/* Anthropic Claude */}
            <div className="border border-blue-200 rounded-lg p-5 bg-blue-50">
              <div className="flex items-center mb-3">
                <Shield size={24} className="mr-3 text-blue-600" />
                <h3 className="text-xl font-bold">Anthropic Claude (4.x/2025)</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Models Available:</h4>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• Claude 4.5 Sonnet (balanced, recommended)</li>
                    <li>• Claude 4 Opus (most capable)</li>
                    <li>• Claude 4 Haiku (fastest, cost-effective)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Context Window:</h4>
                  <p className="text-sm">200K tokens (~150K words)</p>
                  <h4 className="font-semibold text-blue-900 mb-2 mt-3">Best For:</h4>
                  <p className="text-sm">Code analysis, threat modeling, complex reasoning</p>
                </div>
              </div>

              <div className="bg-white rounded p-3 mb-3">
                <h4 className="font-semibold text-blue-900 mb-2">Key Strengths for Cybersecurity:</h4>
                <ul className="text-sm space-y-1">
                  <li>✓ Excellent at understanding security context and nuance</li>
                  <li>✓ Strong code analysis and vulnerability detection</li>
                  <li>✓ Constitutional AI provides built-in safety guardrails</li>
                  <li>✓ Dual isolation: filesystem + network sandboxing</li>
                  <li>✓ Superior at following complex, multi-step instructions</li>
                </ul>
              </div>

              <div className="bg-white rounded p-3">
                <h4 className="font-semibold text-blue-900 mb-2">Prompt Engineering Tips:</h4>
                <ul className="text-sm space-y-1">
                  <li>• Provide clear, explicit instructions with precise details</li>
                  <li>• Explain WHY instructions matter (context is important)</li>
                  <li>• Be careful with examples - Claude follows them literally</li>
                  <li>• Use XML tags for structured output (e.g., {`<vulnerability></vulnerability>`})</li>
                  <li>• Claude 4.x is more precise than predecessors - be specific</li>
                </ul>
              </div>
            </div>

            {/* OpenAI GPT-4 */}
            <div className="border border-purple-200 rounded-lg p-5 bg-purple-50">
              <div className="flex items-center mb-3">
                <Cpu size={24} className="mr-3 text-purple-600" />
                <h3 className="text-xl font-bold">OpenAI GPT-4/GPT-4.1 (2025)</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <h4 className="font-semibold text-purple-900 mb-2">Models Available:</h4>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• GPT-4.1 (latest, most capable)</li>
                    <li>• GPT-4o (optimized, multimodal)</li>
                    <li>• GPT-4 Turbo (fast, cost-effective)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-purple-900 mb-2">Context Window:</h4>
                  <p className="text-sm">128K tokens (~96K words)</p>
                  <h4 className="font-semibold text-purple-900 mb-2 mt-3">Best For:</h4>
                  <p className="text-sm">General security analysis, report writing, broad knowledge</p>
                </div>
              </div>

              <div className="bg-white rounded p-3 mb-3">
                <h4 className="font-semibold text-purple-900 mb-2">Key Strengths for Cybersecurity:</h4>
                <ul className="text-sm space-y-1">
                  <li>✓ Extensive knowledge of security frameworks (NIST, OWASP, etc.)</li>
                  <li>✓ Strong at security report generation and documentation</li>
                  <li>✓ Good tool usage for security automation tasks</li>
                  <li>✓ Multimodal capabilities (analyze images, diagrams)</li>
                  <li>✓ Large ecosystem with many security-focused GPTs</li>
                </ul>
              </div>

              <div className="bg-white rounded p-3">
                <h4 className="font-semibold text-purple-900 mb-2">Prompt Engineering Tips:</h4>
                <ul className="text-sm space-y-1">
                  <li>• GPT-4.1 follows instructions more literally - be direct</li>
                  <li>• Single sentence clarifying desired behavior is usually sufficient</li>
                  <li>• Use tools field exclusively for security tools (not manual injection)</li>
                  <li>• Provide grounding data for reliable, up-to-date responses</li>
                  <li>• Works well with structured JSON outputs for automation</li>
                </ul>
              </div>
            </div>

            {/* Google Gemini */}
            <div className="border border-green-200 rounded-lg p-5 bg-green-50">
              <div className="flex items-center mb-3">
                <Zap size={24} className="mr-3 text-green-600" />
                <h3 className="text-xl font-bold">Google Gemini (2.5 Pro/2025)</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">Models Available:</h4>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• Gemini 2.5 Pro (most capable)</li>
                    <li>• Gemini 2.0 Flash (fast, efficient)</li>
                    <li>• Gemini for Workspace (integrated)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">Context Window:</h4>
                  <p className="text-sm">2M tokens (~1.5M words) - Largest!</p>
                  <h4 className="font-semibold text-green-900 mb-2 mt-3">Best For:</h4>
                  <p className="text-sm">Analyzing massive codebases, long documents, compliance</p>
                </div>
              </div>

              <div className="bg-white rounded p-3 mb-3">
                <h4 className="font-semibold text-green-900 mb-2">Key Strengths for Cybersecurity:</h4>
                <ul className="text-sm space-y-1">
                  <li>✓ Massive 2M token context - analyze entire codebases at once</li>
                  <li>✓ 6,000+ NIST NICE cybersecurity prompts available (free)</li>
                  <li>✓ Strong integration with Google Workspace for security teams</li>
                  <li>✓ Excellent at processing large security logs and documents</li>
                  <li>✓ Google Search integration for latest threat intelligence</li>
                </ul>
              </div>

              <div className="bg-white rounded p-3">
                <h4 className="font-semibold text-green-900 mb-2">Prompt Engineering Tips:</h4>
                <ul className="text-sm space-y-1">
                  <li>• Use consistent structure with XML-style tags or Markdown headings</li>
                  <li>• Be precise and direct, avoid unnecessary language</li>
                  <li>• Leverage massive context window for full codebase analysis</li>
                  <li>• Works well with NIST NICE framework prompts</li>
                  <li>• Download Google's 6,000+ cybersecurity prompt library</li>
                </ul>
              </div>
            </div>

            {/* xAI Grok */}
            <div className="border border-orange-200 rounded-lg p-5 bg-orange-50">
              <div className="flex items-center mb-3">
                <Target size={24} className="mr-3 text-orange-600" />
                <h3 className="text-xl font-bold">xAI Grok (2025)</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <h4 className="font-semibold text-orange-900 mb-2">Models Available:</h4>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• Grok 2 (latest)</li>
                    <li>• Grok 2 mini (efficient)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-orange-900 mb-2">Context Window:</h4>
                  <p className="text-sm">131K tokens (~98K words)</p>
                  <h4 className="font-semibold text-orange-900 mb-2 mt-3">Best For:</h4>
                  <p className="text-sm">Real-time threat intelligence, X/Twitter data analysis</p>
                </div>
              </div>

              <div className="bg-white rounded p-3 mb-3">
                <h4 className="font-semibold text-orange-900 mb-2">Key Strengths for Cybersecurity:</h4>
                <ul className="text-sm space-y-1">
                  <li>✓ Real-time access to X (Twitter) for latest threat intelligence</li>
                  <li>✓ Up-to-date information on emerging threats and vulnerabilities</li>
                  <li>✓ Good at analyzing social engineering and phishing campaigns</li>
                  <li>✓ Strong reasoning capabilities for complex security scenarios</li>
                  <li>✓ Less censored - useful for red team exercises (use responsibly)</li>
                </ul>
              </div>

              <div className="bg-white rounded p-3">
                <h4 className="font-semibold text-orange-900 mb-2">Prompt Engineering Tips:</h4>
                <ul className="text-sm space-y-1">
                  <li>• Leverage real-time X data for current threat intelligence</li>
                  <li>• Good for tracking APT groups and threat actors on social media</li>
                  <li>• Use for OSINT and reconnaissance planning</li>
                  <li>• More conversational style works well</li>
                  <li>• Useful for red team scenario generation</li>
                </ul>
              </div>
            </div>

            {/* DeepSeek */}
            <div className="border border-indigo-200 rounded-lg p-5 bg-indigo-50">
              <div className="flex items-center mb-3">
                <Code size={24} className="mr-3 text-indigo-600" />
                <h3 className="text-xl font-bold">DeepSeek (V3/2025)</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <h4 className="font-semibold text-indigo-900 mb-2">Models Available:</h4>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• DeepSeek-V3 (latest)</li>
                    <li>• DeepSeek-Coder (specialized)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-indigo-900 mb-2">Context Window:</h4>
                  <p className="text-sm">128K tokens (~96K words)</p>
                  <h4 className="font-semibold text-indigo-900 mb-2 mt-3">Best For:</h4>
                  <p className="text-sm">Code security analysis, vulnerability research</p>
                </div>
              </div>

              <div className="bg-white rounded p-3 mb-3">
                <h4 className="font-semibold text-indigo-900 mb-2">Key Strengths for Cybersecurity:</h4>
                <ul className="text-sm space-y-1">
                  <li>✓ Exceptional code analysis and understanding</li>
                  <li>✓ Strong at identifying code-level vulnerabilities</li>
                  <li>✓ Cost-effective open-source alternative</li>
                  <li>✓ Good reasoning capabilities for security logic</li>
                  <li>✓ Can be self-hosted for sensitive security work</li>
                </ul>
              </div>

              <div className="bg-white rounded p-3">
                <h4 className="font-semibold text-indigo-900 mb-2">Prompt Engineering Tips:</h4>
                <ul className="text-sm space-y-1">
                  <li>• Excellent for secure code review and SAST tasks</li>
                  <li>• Use DeepSeek-Coder variant for maximum code understanding</li>
                  <li>• Works well with technical, code-focused prompts</li>
                  <li>• Good for identifying specific vulnerability patterns</li>
                  <li>• Cost-effective for large-scale security audits</li>
                </ul>
              </div>
            </div>

            {/* Meta Llama */}
            <div className="border border-cyan-200 rounded-lg p-5 bg-cyan-50">
              <div className="flex items-center mb-3">
                <Server size={24} className="mr-3 text-cyan-600" />
                <h3 className="text-xl font-bold">Meta Llama (3.3/2025)</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <h4 className="font-semibold text-cyan-900 mb-2">Models Available:</h4>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• Llama 3.3 70B (most capable)</li>
                    <li>• Llama 3.2 (lightweight)</li>
                    <li>• Llama Guard (safety-focused)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-cyan-900 mb-2">Context Window:</h4>
                  <p className="text-sm">128K tokens (~96K words)</p>
                  <h4 className="font-semibold text-cyan-900 mb-2 mt-3">Best For:</h4>
                  <p className="text-sm">Self-hosted security, privacy-focused work, on-prem</p>
                </div>
              </div>

              <div className="bg-white rounded p-3 mb-3">
                <h4 className="font-semibold text-cyan-900 mb-2">Key Strengths for Cybersecurity:</h4>
                <ul className="text-sm space-y-1">
                  <li>✓ Open source - full control and auditability</li>
                  <li>✓ Can be self-hosted for maximum security and privacy</li>
                  <li>✓ Llama Guard provides content moderation capabilities</li>
                  <li>✓ Strong community support and security fine-tunes</li>
                  <li>✓ No data sent to third parties - ideal for sensitive work</li>
                </ul>
              </div>

              <div className="bg-white rounded p-3">
                <h4 className="font-semibold text-cyan-900 mb-2">Prompt Engineering Tips:</h4>
                <ul className="text-sm space-y-1">
                  <li>• Best for organizations requiring on-premises AI</li>
                  <li>• Use Llama Guard for testing AI safety mechanisms</li>
                  <li>• Can be fine-tuned on organization-specific security data</li>
                  <li>• Good for compliance-heavy industries (finance, healthcare)</li>
                  <li>• Community fine-tunes available for security tasks</li>
                </ul>
              </div>
            </div>

            {/* Mistral */}
            <div className="border border-red-200 rounded-lg p-5 bg-red-50">
              <div className="flex items-center mb-3">
                <Zap size={24} className="mr-3 text-red-600" />
                <h3 className="text-xl font-bold">Mistral AI (Large 2/2025)</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <h4 className="font-semibold text-red-900 mb-2">Models Available:</h4>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• Mistral Large 2 (most capable)</li>
                    <li>• Mistral Small (cost-effective)</li>
                    <li>• Codestral (code-specialized)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-red-900 mb-2">Context Window:</h4>
                  <p className="text-sm">128K tokens (~96K words)</p>
                  <h4 className="font-semibold text-red-900 mb-2 mt-3">Best For:</h4>
                  <p className="text-sm">European compliance, fast responses, code analysis</p>
                </div>
              </div>

              <div className="bg-white rounded p-3 mb-3">
                <h4 className="font-semibold text-red-900 mb-2">Key Strengths for Cybersecurity:</h4>
                <ul className="text-sm space-y-1">
                  <li>✓ EU-based - good for GDPR and European compliance</li>
                  <li>✓ Fast inference - great for real-time security monitoring</li>
                  <li>✓ Codestral excellent for security code analysis</li>
                  <li>✓ Strong multilingual support for international teams</li>
                  <li>✓ Cost-effective with competitive performance</li>
                </ul>
              </div>

              <div className="bg-white rounded p-3">
                <h4 className="font-semibold text-red-900 mb-2">Prompt Engineering Tips:</h4>
                <ul className="text-sm space-y-1">
                  <li>• Use Codestral for security code review tasks</li>
                  <li>• Good for European organizations with data residency requirements</li>
                  <li>• Fast response times suitable for real-time security tools</li>
                  <li>• Works well in multiple languages for global security teams</li>
                  <li>• Can be self-hosted via open-source variants</li>
                </ul>
              </div>
            </div>

            {/* Perplexity */}
            <div className="border border-teal-200 rounded-lg p-5 bg-teal-50">
              <div className="flex items-center mb-3">
                <Database size={24} className="mr-3 text-teal-600" />
                <h3 className="text-xl font-bold">Perplexity AI (2025)</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <h4 className="font-semibold text-teal-900 mb-2">Models Available:</h4>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• Perplexity Pro (multi-model)</li>
                    <li>• Uses Claude, GPT-4, others</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-teal-900 mb-2">Context Window:</h4>
                  <p className="text-sm">Varies by underlying model</p>
                  <h4 className="font-semibold text-teal-900 mb-2 mt-3">Best For:</h4>
                  <p className="text-sm">Security research, CVE lookup, threat intelligence</p>
                </div>
              </div>

              <div className="bg-white rounded p-3 mb-3">
                <h4 className="font-semibold text-teal-900 mb-2">Key Strengths for Cybersecurity:</h4>
                <ul className="text-sm space-y-1">
                  <li>✓ Real-time web search with citations</li>
                  <li>✓ Excellent for researching CVEs and vulnerabilities</li>
                  <li>✓ Provides sources for threat intelligence</li>
                  <li>✓ Up-to-date information on latest security news</li>
                  <li>✓ Good for competitive intelligence and OSINT</li>
                </ul>
              </div>

              <div className="bg-white rounded p-3">
                <h4 className="font-semibold text-teal-900 mb-2">Prompt Engineering Tips:</h4>
                <ul className="text-sm space-y-1">
                  <li>• Best for researching specific CVEs and exploits</li>
                  <li>• Use for staying current on emerging threats</li>
                  <li>• Citations help verify security intelligence</li>
                  <li>• Good for security blog research and vendor advisories</li>
                  <li>• Ask for sources to validate threat information</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Quick Comparison Table */}
          <div className="mt-8">
            <h3 className="text-xl font-bold mb-4">Quick Comparison Matrix</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 border">Model</th>
                    <th className="px-4 py-2 border">Context Size</th>
                    <th className="px-4 py-2 border">Cost</th>
                    <th className="px-4 py-2 border">Code Analysis</th>
                    <th className="px-4 py-2 border">Real-time Data</th>
                    <th className="px-4 py-2 border">Self-Hosted</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-2 border font-semibold">Claude 4.5</td>
                    <td className="px-4 py-2 border">200K</td>
                    <td className="px-4 py-2 border">$$$</td>
                    <td className="px-4 py-2 border text-center">⭐⭐⭐⭐⭐</td>
                    <td className="px-4 py-2 border text-center">❌</td>
                    <td className="px-4 py-2 border text-center">❌</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 border font-semibold">GPT-4.1</td>
                    <td className="px-4 py-2 border">128K</td>
                    <td className="px-4 py-2 border">$$$</td>
                    <td className="px-4 py-2 border text-center">⭐⭐⭐⭐</td>
                    <td className="px-4 py-2 border text-center">❌</td>
                    <td className="px-4 py-2 border text-center">❌</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border font-semibold">Gemini 2.5 Pro</td>
                    <td className="px-4 py-2 border">2M ⚡</td>
                    <td className="px-4 py-2 border">$$</td>
                    <td className="px-4 py-2 border text-center">⭐⭐⭐⭐</td>
                    <td className="px-4 py-2 border text-center">✅</td>
                    <td className="px-4 py-2 border text-center">❌</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 border font-semibold">Grok 2</td>
                    <td className="px-4 py-2 border">131K</td>
                    <td className="px-4 py-2 border">$$</td>
                    <td className="px-4 py-2 border text-center">⭐⭐⭐</td>
                    <td className="px-4 py-2 border text-center">✅ (X)</td>
                    <td className="px-4 py-2 border text-center">❌</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border font-semibold">DeepSeek V3</td>
                    <td className="px-4 py-2 border">128K</td>
                    <td className="px-4 py-2 border">$</td>
                    <td className="px-4 py-2 border text-center">⭐⭐⭐⭐⭐</td>
                    <td className="px-4 py-2 border text-center">❌</td>
                    <td className="px-4 py-2 border text-center">✅</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 border font-semibold">Llama 3.3</td>
                    <td className="px-4 py-2 border">128K</td>
                    <td className="px-4 py-2 border">Free</td>
                    <td className="px-4 py-2 border text-center">⭐⭐⭐</td>
                    <td className="px-4 py-2 border text-center">❌</td>
                    <td className="px-4 py-2 border text-center">✅</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border font-semibold">Mistral Large 2</td>
                    <td className="px-4 py-2 border">128K</td>
                    <td className="px-4 py-2 border">$$</td>
                    <td className="px-4 py-2 border text-center">⭐⭐⭐⭐</td>
                    <td className="px-4 py-2 border text-center">❌</td>
                    <td className="px-4 py-2 border text-center">✅</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 border font-semibold">Perplexity</td>
                    <td className="px-4 py-2 border">Varies</td>
                    <td className="px-4 py-2 border">$$</td>
                    <td className="px-4 py-2 border text-center">⭐⭐⭐</td>
                    <td className="px-4 py-2 border text-center">✅</td>
                    <td className="px-4 py-2 border text-center">❌</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Recommendations */}
          <div className="mt-8 p-4 bg-gray-100 rounded-lg">
            <h3 className="text-lg font-bold mb-3">💡 Recommendations by Use Case</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-semibold mb-1">🔍 Vulnerability Analysis:</p>
                <p>Claude 4.5 Sonnet, DeepSeek-Coder</p>
              </div>
              <div>
                <p className="font-semibold mb-1">📊 Report Writing:</p>
                <p>GPT-4.1, Claude 4 Opus</p>
              </div>
              <div>
                <p className="font-semibold mb-1">🔐 Privacy-Critical Work:</p>
                <p>Llama 3.3 (self-hosted), DeepSeek V3</p>
              </div>
              <div>
                <p className="font-semibold mb-1">🌐 Threat Intelligence:</p>
                <p>Grok 2, Perplexity, Gemini 2.5</p>
              </div>
              <div>
                <p className="font-semibold mb-1">💰 Cost-Effective:</p>
                <p>Llama 3.3 (free), DeepSeek, Mistral</p>
              </div>
              <div>
                <p className="font-semibold mb-1">📚 Large Codebases:</p>
                <p>Gemini 2.5 Pro (2M context!)</p>
              </div>
              <div>
                <p className="font-semibold mb-1">🇪🇺 EU Compliance:</p>
                <p>Mistral AI, self-hosted Llama</p>
              </div>
              <div>
                <p className="font-semibold mb-1">🔴 Red Team Exercises:</p>
                <p>Grok 2, Claude 4.5, Llama (self-hosted)</p>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-900">
              <strong>Pro Tip:</strong> For maximum effectiveness, use multiple models for different tasks in your security workflow.
              For example: Use Gemini for initial codebase analysis (2M context), Claude for detailed vulnerability assessment,
              and Perplexity for researching discovered CVEs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const WelcomeModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
          <h2 className="text-2xl font-bold mb-2">👋 Welcome to the Cybersecurity Prompt Engineering Tool</h2>
          <p className="text-blue-100">Your comprehensive resource for AI-powered security workflows</p>
        </div>

        <div className="p-6">
          {/* What is this tool */}
          <div className="mb-6">
            <h3 className="text-xl font-bold mb-3 flex items-center text-gray-800">
              <Shield size={24} className="mr-2 text-blue-600" />
              What is This Tool?
            </h3>
            <p className="text-gray-700 mb-3">
              This is a free, open-source collection of <strong>expert-crafted prompts</strong> designed specifically for
              cybersecurity professionals using AI assistants like Claude, GPT-4, Gemini, and others.
            </p>
            <p className="text-gray-700">
              We've created 50+ specialized prompts covering everything from vulnerability assessment to incident response,
              all following the latest 2025 best practices from Anthropic, OpenAI, Google, and OWASP.
            </p>
          </div>

          {/* What is Prompt Engineering */}
          <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-lg font-bold mb-2 flex items-center text-purple-900">
              <Code size={20} className="mr-2" />
              What is Prompt Engineering?
            </h3>
            <p className="text-gray-700 mb-2">
              Prompt engineering is the practice of crafting effective instructions for AI models to get better,
              more accurate, and more useful results.
            </p>
            <p className="text-gray-700 text-sm">
              A well-engineered prompt can mean the difference between generic advice and actionable security intelligence
              tailored to your specific needs.
            </p>
          </div>

          {/* How it Works */}
          <div className="mb-6">
            <h3 className="text-xl font-bold mb-3 flex items-center text-gray-800">
              <Zap size={24} className="mr-2 text-green-600" />
              How It Works
            </h3>
            <ol className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="font-bold text-blue-600 mr-2">1.</span>
                <span>Browse our categories or search for a specific security task</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-blue-600 mr-2">2.</span>
                <span>Click any prompt to view and copy it</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-blue-600 mr-2">3.</span>
                <span>Paste into your AI tool (Claude, GPT-4, Gemini, etc.)</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-blue-600 mr-2">4.</span>
                <span>Replace [PLACEHOLDERS] with your specific information</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-blue-600 mr-2">5.</span>
                <span>Get professional-quality security analysis and recommendations!</span>
              </li>
            </ol>
          </div>

          {/* Quick Links */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-bold mb-3 text-gray-800">🚀 Explore More</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => {
                  setShowWelcomeModal(false);
                  setShowModal(true);
                }}
                className="flex flex-col items-center p-3 bg-white border-2 border-blue-200 rounded-lg hover:border-blue-400 transition duration-150"
              >
                <Info size={24} className="text-blue-600 mb-2" />
                <span className="text-sm font-medium text-center">How It Works</span>
                <span className="text-xs text-gray-500">Detailed guide</span>
              </button>
              <button
                onClick={() => {
                  setShowWelcomeModal(false);
                  setShowResourcesModal(true);
                }}
                className="flex flex-col items-center p-3 bg-white border-2 border-green-200 rounded-lg hover:border-green-400 transition duration-150"
              >
                <BookMarked size={24} className="text-green-600 mb-2" />
                <span className="text-sm font-medium text-center">Resources</span>
                <span className="text-xs text-gray-500">Latest research</span>
              </button>
              <button
                onClick={() => {
                  setShowWelcomeModal(false);
                  setShowModelComparisonModal(true);
                }}
                className="flex flex-col items-center p-3 bg-white border-2 border-purple-200 rounded-lg hover:border-purple-400 transition duration-150"
              >
                <GitCompare size={24} className="text-purple-600 mb-2" />
                <span className="text-sm font-medium text-center">Model Comparison</span>
                <span className="text-xs text-gray-500">Choose the right AI</span>
              </button>
            </div>
          </div>

          {/* Partners */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3 text-gray-800">🤝 Our DevSecOps Partners</h3>
            <p className="text-sm text-gray-600 mb-3">
              This tool is part of a comprehensive DevSecOps ecosystem:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              <a href="https://cyberagent.exchange/" target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-50 rounded hover:bg-blue-100 text-center">
                Cyber Agent Exchange
              </a>
              <a href="https://airiskassess.com/" target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-50 rounded hover:bg-blue-100 text-center">
                AI Risk Assessment
              </a>
              <a href="https://cybersecuritygpt.store" target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-50 rounded hover:bg-blue-100 text-center">
                Cybersecurity GPT Store
              </a>
              <a href="https://cisomarketplace.services/" target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-50 rounded hover:bg-blue-100 text-center">
                CISO Marketplace
              </a>
              <a href="https://vibehack.dev/" target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-50 rounded hover:bg-blue-100 text-center">
                VibeHack
              </a>
              <a href="https://compliance.airiskassess.com/" target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-50 rounded hover:bg-blue-100 text-center">
                AI Risk Compliance
              </a>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <button
              onClick={() => handleCloseWelcome(true)}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition duration-150"
            >
              Got it! Start Using Prompts
            </button>
            <button
              onClick={() => handleCloseWelcome(false)}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium transition duration-150"
            >
              Remind Me Next Time
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen max-h-screen bg-gray-100 text-gray-900">
      <header className="bg-gray-800 text-white p-4 flex items-center justify-between shadow-md">
        <div className="flex items-center">
          <BookOpen className="mr-2" size={24} />
          <h1 className="text-xl font-bold">Cybersecurity Prompt Engineering Tool</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded"
          >
            <Info size={18} className="mr-1" />
            How It Works
          </button>
          <button
            onClick={() => setShowModelComparisonModal(true)}
            className="flex items-center bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded"
          >
            <GitCompare size={18} className="mr-1" />
            Model Comparison
          </button>
          <button
            onClick={() => setShowResourcesModal(true)}
            className="flex items-center bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded"
          >
            <BookMarked size={18} className="mr-1" />
            Resources
          </button>
        </div>
      </header>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 bg-gray-800 text-white overflow-y-auto">
          <div className="p-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search categories & prompts..."
                className="w-full bg-gray-700 text-white p-2 pl-8 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-2 top-2.5 text-gray-400" size={16} />
            </div>
          </div>
          
          <nav className="mt-2">
            {filteredCategories.map((category) => (
              <button
                key={category.id}
                className={`w-full text-left p-3 flex items-center hover:bg-gray-700 ${
                  selectedCategory === category.id ? 'bg-gray-700 border-l-4 border-blue-500' : ''
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className="mr-3">{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </nav>
        </div>
        
        {/* Main content */}
        <div className="flex-1 overflow-y-auto flex flex-col">
          <div className="flex-1 p-6">
            {selectedCategory && (
              <>
                <h2 className="text-2xl font-bold mb-6 flex items-center">
                  {categories.find(c => c.id === selectedCategory)?.icon} 
                  <span className="ml-2">{categories.find(c => c.id === selectedCategory)?.name} Prompts</span>
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredPrompts.map((prompt: Prompt) => (
                    <div key={prompt.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                      <div className="p-5">
                        <h3 className="text-lg font-bold mb-2">{prompt.title}</h3>
                        <p className="text-gray-600 mb-4">{prompt.description}</p>
                        
                        <div className="flex justify-between items-center">
                          <button
                            className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                            onClick={() => {
                              const promptElement = document.getElementById(`prompt-${prompt.id}`);
                              if (promptElement?.classList.contains('hidden')) {
                                promptElement.classList.remove('hidden');
                              } else if (promptElement) {
                                promptElement.classList.add('hidden');
                              }
                            }}
                          >
                            <span>View Prompt</span>
                          </button>
                          
                          <button
                            className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                            onClick={() => copyToClipboard(prompt.prompt)}
                          >
                            {copiedId === prompt.prompt ? 'Copied!' : 'Copy'}
                            <Copy size={16} className="ml-1" />
                          </button>
                        </div>
                      </div>
                      
                      <div id={`prompt-${prompt.id}`} className="hidden bg-gray-100 p-4 mt-2 border-t border-gray-200">
                        <pre className="whitespace-pre-wrap text-sm font-mono">{prompt.prompt}</pre>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {!selectedCategory && (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <Shield size={64} />
                <h2 className="text-xl mt-4 mb-2">Select a cybersecurity category</h2>
                <p>Browse prompts and templates organized by security domain</p>
              </div>
            )}
          </div>
          
          {/* Info section above footer */}
          <div className="bg-gray-100 border-t border-gray-200 p-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-4 text-center">Enhance Your Cybersecurity Work with AI</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <h3 className="font-bold text-lg mb-2">Expert-Crafted Prompts</h3>
                  <p>Our prompts are designed by cybersecurity experts to generate high-quality, accurate, and comprehensive security content across multiple domains.</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <h3 className="font-bold text-lg mb-2">Save Time & Effort</h3>
                  <p>Skip the trial-and-error of prompt engineering. Use our templates to immediately get useful results from your AI assistant for security tasks.</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <h3 className="font-bold text-lg mb-2">Versatile Applications</h3>
                  <p>Use these prompts for security assessments, report writing, policy development, security architecture, threat intelligence, and more.</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sponsors section */}
          <div className="bg-white border-t border-gray-200 py-8">
            <div className="max-w-6xl mx-auto px-4">
              <h2 className="text-xl font-bold mb-6 text-center">Our Partners & Sponsors</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <a 
                  href="https://cyberagent.exchange/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center p-4 border rounded-lg hover:bg-gray-50 transition duration-150"
                >
                  <div className="w-16 h-16 bg-gray-200 rounded-full mb-3 flex items-center justify-center">
                    <Shield size={32} className="text-blue-600" />
                  </div>
                  <span className="font-medium text-center">Cyber Agent Exchange</span>
                </a>
                <a 
                  href="https://airiskassess.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center p-4 border rounded-lg hover:bg-gray-50 transition duration-150"
                >
                  <div className="w-16 h-16 bg-gray-200 rounded-full mb-3 flex items-center justify-center">
                    <AlertTriangle size={32} className="text-orange-600" />
                  </div>
                  <span className="font-medium text-center">AI Risk Assessment</span>
                </a>
                <a 
                  href="https://cybersecuritygpt.store" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center p-4 border rounded-lg hover:bg-gray-50 transition duration-150"
                >
                  <div className="w-16 h-16 bg-gray-200 rounded-full mb-3 flex items-center justify-center">
                    <Zap size={32} className="text-green-600" />
                  </div>
                  <span className="font-medium text-center">Cybersecurity GPT Store</span>
                </a>
                <a 
                  href="https://cisomarketplace.services/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center p-4 border rounded-lg hover:bg-gray-50 transition duration-150"
                >
                  <div className="w-16 h-16 bg-gray-200 rounded-full mb-3 flex items-center justify-center">
                    <Briefcase size={32} className="text-purple-600" />
                  </div>
                  <span className="font-medium text-center">CISO Marketplace</span>
                </a>
              </div>

              <div className="mt-8">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <h3 className="text-lg font-bold mb-3 text-green-900">📚 Explore More Resources</h3>
                  <p className="text-sm text-green-800 mb-4">
                    Access comprehensive prompt engineering guides, latest 2025 research from Anthropic, Google, OpenAI, OWASP, and more!
                  </p>
                  <button
                    onClick={() => setShowResourcesModal(true)}
                    className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium shadow-md transition duration-150"
                  >
                    <BookMarked size={20} className="mr-2" />
                    View All Prompt Engineering Resources
                  </button>
                </div>

                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold mb-3 text-blue-900 flex items-center justify-center">
                    <Mic size={20} className="mr-2" />
                    CISO Marketplace Podcast
                  </h3>
                  <p className="text-sm text-blue-800 mb-4 text-center">
                    Listen to in-depth discussions on AI security, LLM vulnerabilities, and emerging threats
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center">
                    <a
                      href="https://podcast.cisomarketplace.com/?s=ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-150"
                    >
                      <Mic size={16} className="mr-2" />
                      <span>AI Security Episodes</span>
                      <ExternalLink size={14} className="ml-2" />
                    </a>
                    <a
                      href="https://podcast.cisomarketplace.com/?s=llm"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-150"
                    >
                      <Shield size={16} className="mr-2" />
                      <span>LLM Security Episodes</span>
                      <ExternalLink size={14} className="ml-2" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <footer className="bg-gray-800 text-white p-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-4">
                <p className="text-lg">Cybersecurity Prompt Engineering Tool — Helping security professionals leverage AI assistants effectively</p>
                <p className="text-sm mt-2 text-gray-400">This tool provides prompt templates only. Always verify AI-generated security content for accuracy.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 text-sm text-gray-400 border-t border-gray-700 pt-4">
                <div>
                  <h4 className="text-white font-medium mb-2">Cybersecurity Domains</h4>
                  <ul className="space-y-1">
                    <li>Vulnerability Assessment</li>
                    <li>Penetration Testing</li>
                    <li>Threat Modeling</li>
                    <li>Security Architecture</li>
                    <li>Incident Response</li>
                    <li>Security Awareness</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-2">AI Prompt Resources</h4>
                  <ul className="space-y-1">
                    <li>Prompt Engineering Best Practices</li>
                    <li>Security-focused LLM Strategies</li>
                    <li>Contextual Prompt Design</li>
                    <li>Effective Cybersecurity Questioning</li>
                    <li>AI-assisted Security Assessment</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-2">About This Tool</h4>
                  <p>The Cybersecurity Prompt Engineering Tool helps security professionals create effective prompts for AI language models when working on cybersecurity tasks. Our expert-crafted templates cover vulnerability assessment, penetration testing, threat modeling, security architecture, and more.</p>
                  
                  <div className="mt-3">
                    <p>Available at:</p>
                    <a href="https://prompts.cyberagent.exchange" className="text-blue-400 hover:text-blue-300">prompts.cyberagent.exchange</a>
                    <span className="mx-2">|</span>
                    <a href="https://prompts.cybersecuritygpt.store" className="text-blue-400 hover:text-blue-300">prompts.cybersecuritygpt.store</a>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-6 text-xs text-gray-500 border-t border-gray-700 pt-4">
                <p>© {new Date().getFullYear()} Cybersecurity Prompt Engineering Tool. All rights reserved.</p>
                <p className="mt-1">Last updated: November 2025</p>
              </div>
            </div>
          </footer>
        </div>
      </div>

      {/* Modals */}
      {showWelcomeModal && <WelcomeModal />}
      {showModal && <HowItWorksModal />}
      {showModelComparisonModal && <ModelComparisonModal />}
      {showResourcesModal && <ResourcesModal />}
    </div>
  );
};

export default CyberPromptTool; 